import type { CoverSheetDocument } from '../document';

type ReplaceNullWithEmptyString<T> = T extends null
  ? ''
  : T extends (infer U)[]
    ? ReplaceNullWithEmptyString<U>[]
    : T extends object
      ? { [K in keyof T]: ReplaceNullWithEmptyString<T[K]> }
      : T;

// biome-ignore lint/suspicious/noExplicitAny: Preserve existing implementation
const convertAllNullsToEmptyString = <T extends Record<string | number, any>>(
  obj: T,
) => {
  if (!obj) return obj;
  // biome-ignore lint/suspicious/noExplicitAny: Preserve existing implementation
  const updated: Record<string | number, any> = { ...obj };
  Object.keys(obj).forEach((k) => {
    if (updated[k] === null) updated[k] = '';
    if (typeof updated[k] === 'object') {
      if (Array.isArray(updated[k])) {
        updated[k] = (updated[k] as unknown[]).map((item: unknown) =>
          // biome-ignore lint/suspicious/noExplicitAny: Preserve existing implementation
          convertAllNullsToEmptyString(item as Record<string, any>),
        );
      } else {
        updated[k] = convertAllNullsToEmptyString(
          // biome-ignore lint/suspicious/noExplicitAny: Preserve existing implementation
          updated[k] as Record<string, any>,
        );
      }
    }
  });

  return updated as ReplaceNullWithEmptyString<T>;
};

// The cover-sheet form holds the property-info entities only; contacts render
// through the contacts engine, which reads them from its own form.
export type CoverSheetFormData = Partial<CoverSheetDocument>;

export type SanitizedCoverSheetFormData =
  ReplaceNullWithEmptyString<CoverSheetFormData>;

export function transformCoverSheetToFormData(
  coverSheetData: Partial<CoverSheetDocument>,
  convertNullsToEmptyString?: true,
): SanitizedCoverSheetFormData;
export function transformCoverSheetToFormData(
  coverSheetData: Partial<CoverSheetDocument>,
  convertNullsToEmptyString?: false,
): CoverSheetFormData;
export function transformCoverSheetToFormData(
  coverSheetData: Partial<CoverSheetDocument>,
  convertNullsToEmptyString = true,
) {
  // Default commission type so amount field (percent/dollar) is visible when API returns null
  const formData: CoverSheetFormData = {
    ...coverSheetData,
    ...(!coverSheetData.commissionType && {
      commissionType: 'PERCENTAGE' as const,
    }),
    ...(!coverSheetData.counterpartyCommissionType && {
      counterpartyCommissionType: 'PERCENTAGE' as const,
    }),
  };

  return convertNullsToEmptyString
    ? convertAllNullsToEmptyString(formData)
    : formData;
}
